// KYCPage.tsx
import { Button, Flex, Select, Title } from "@mantine/core";
import { DatePickerInput, DatesRangeValue } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions";
import { useCreateKYC } from "../../api/hooks/kyc/useCreateKYC";
import { useTrack } from "../../api/hooks/user/useTrack.ts";
import SvgCalendar from "../../components/Icons/Calendar";
import Layout from "../../components/Layout/Layout";
import Preloader from "../../components/Preloader/Preloader";
import Table from "../../components/Table/Table";
import { SortDirection, TableRow } from "../../components/Table/types";
import { ActivityTrackerTypes } from "../../graphql/generated.ts";
import { getCompanyOptions } from "../../utils/company";
import { getDateTimeRangeFilter } from "../../utils/filter";
import { getPagesCount } from "../../utils/pagination";
import ErrorKYCModal from "./components/ErrorKYCModal";
import InputDataCompanyModal from "./components/InputDataCompanyModal";
import ProcessingKYCModal from "./components/ProcessingKYCModal";
import { useKYCData } from "./hooks/useKYCData";
import { ExtendedKYCTableRow, useKYCTable } from "./hooks/useKYCTable";
import { CompanyKYCFormData, KYCEntry, KYCStatus } from "./types";

const KYCPage = () => {
  const navigate = useNavigate();
  const [requestDateRange, setRequestDateRange] = useState<DatesRangeValue>([
    null,
    null,
  ]);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [, track] = useTrack();

  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorReason, setErrorReason] = useState<string | undefined>(undefined);

  const [
    showCompanyModal,
    { open: openCompanyModal, close: closeCompanyModal },
  ] = useDisclosure(false);

  const [
    showProcessingModal,
    { open: openProcessingModal, close: closeProcessingModal },
  ] = useDisclosure(false);

  const [showErrorModal, { open: openErrorModal, close: closeErrorModal }] =
    useDisclosure(false);

  const [{ data: customersResponse, fetching: fetchingCustomers }] =
    useCompanyOptions();
  const customers = customersResponse?.customers.data;

  const { createKYC, fetching: creatingKYC } = useCreateKYC();

  const requestDateFilter = getDateTimeRangeFilter(requestDateRange);

  useEffect(() => {
    setPage(1);
  }, [requestDateRange, customerId]);

  const {
    data: kycResponse,
    loading,
    error,
    refetch,
  } = useKYCData({
    page,
    limit: 10,
    sortField,
    sortDirection,
    filters: {
      dateRange: requestDateFilter,
      customerId,
    },
  });

  const kycData = kycResponse?.data || [];
  const pageCount = getPagesCount(kycResponse?.total);

  const [columns, rows] = useKYCTable({
    data: kycData as unknown as KYCEntry[],
  });

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSortChange = useCallback((sort: DataTableSortStatus) => {
    setSortField(sort.columnAccessor);
    setSortDirection(sort.direction);
  }, []);

  useEffect(() => {
    const hasOngoing = (rows as ExtendedKYCTableRow[]).some(
      (r) => r?.rawStatus === KYCStatus.ONGOING
    );

    const shouldPoll =
      hasOngoing &&
      !showCompanyModal &&
      !showProcessingModal &&
      !showErrorModal;

    if (!shouldPoll) return;

    const intervalId = setInterval(() => {
      refetch();
    }, 15000);

    return () => clearInterval(intervalId);
  }, [rows, showCompanyModal, showProcessingModal, showErrorModal, refetch]);

  const handleRowClick = useCallback(
    (record: ExtendedKYCTableRow) => {
      if (record.rawStatus === KYCStatus.ONGOING) {
        openProcessingModal();
      } else if (record.rawStatus === KYCStatus.FAILED) {
        setErrorReason(record.errorReason);
        openErrorModal();
      } else {
        track({
          input: {
            eventType: ActivityTrackerTypes.ClickViewResult,
            metadata: {
              kyc_id: record?.id,
              customerId: record?.customerId,
            },
          },
        });
        navigate(`/kyc/detail?id=${record.id}&customerId=${record.customerId}`);
      }
    },
    [navigate, openProcessingModal, openErrorModal, track]
  );

  const handleCompanySubmit = async (data: CompanyKYCFormData) => {
    closeCompanyModal();
    setIsSubmitting(true);

    try {
      // Files are already uploaded in the modal, we receive URLs
      const result = await createKYC({
        customerId: data.customerId,
        ktp_ids: data.ktpIds,
        npwp_company_id: data.npwpCompanyId,
        npwp_individu_ids:
          data.npwpIndividuIds && data.npwpIndividuIds.length > 0
            ? data.npwpIndividuIds
            : undefined,
        ahu_url: data.ahuUrl,
        nib_url: data.nibUrl,
        newest_akta_url: data.newestAktaUrl,
        pendirian_akta_url: data.pendirianAktaUrl,
        passport_urls:
          data.passportUrls && data.passportUrls.length > 0
            ? data.passportUrls
            : undefined,
      });

      if (result.data?.createKYC) {
        notifications.show({
          title: "Success",
          message: "KYC request created successfully",
          color: "green",
        });

        // Refetch the table data
        refetch();

        // Show processing modal with the new KYC's createdAt
        openProcessingModal();
      } else if (result.error) {
        notifications.show({
          title: "Error",
          message: result.error.message || "Failed to create KYC request",
          color: "red",
        });
      }
    } catch (err) {
      console.error("KYC submission error:", err);
      notifications.show({
        title: "Error",
        message: "An unexpected error occurred",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <Layout>
        <Flex
          direction="column"
          align="center"
          justify="center"
          style={{ height: "50vh" }}
        >
          <Title order={3} color="red">
            Error loading data
          </Title>
          <p>Please try again later.</p>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Preloader loading={fetchingCustomers || creatingKYC || isSubmitting} />
      <Flex direction="column" gap={20}>
        <Flex justify="space-between" align="center">
          <Title size="h1" style={{ flexShrink: 0 }}>
            Know Your Customer (KYC)
          </Title>

          <Button
            variant="filled"
            size="md"
            uppercase
            onClick={openCompanyModal}
            style={{
              backgroundColor: "#1a2332",
              borderRadius: "24px",
              padding: "12px 32px",
            }}
          >
            Request Data
          </Button>
        </Flex>

        <Flex gap={16} align="flex-end">
          <Select
            label="Company Name"
            placeholder="Select Company Name"
            value={customerId}
            onChange={setCustomerId}
            data={getCompanyOptions(customers)}
            searchable
            clearable
            size="m"
            disabled={fetchingCustomers}
            style={{ flex: 1 }}
          />

          <DatePickerInput
            label="Request Date"
            placeholder="Select Date"
            icon={<SvgCalendar />}
            firstDayOfWeek={0}
            type="range"
            size="m"
            clearable
            value={requestDateRange}
            onChange={setRequestDateRange}
            style={{ flex: 1 }}
          />
        </Flex>

        <Table
          loading={loading}
          columns={columns}
          rows={rows}
          defaultSort={{
            columnAccessor: "createdAt",
            direction: SortDirection.desc,
          }}
          pagination={{
            pageCount: pageCount,
            page: page,
            onPageChange: handlePageChange,
          }}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick as (record: TableRow) => void}
          highlightOnHover
        />
      </Flex>

      <InputDataCompanyModal
        opened={showCompanyModal}
        onClose={closeCompanyModal}
        onSubmit={handleCompanySubmit}
      />

      <ProcessingKYCModal
        opened={showProcessingModal}
        onClose={closeProcessingModal}
      />

      <ErrorKYCModal
        opened={showErrorModal}
        onClose={closeErrorModal}
        reason={errorReason}
      />
    </Layout>
  );
};

export default KYCPage;
